import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Building } from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

const AdminQuotes = () => {
  const [quotes, setQuotes] = useState([]);

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API}/quotes`);
      setQuotes(response.data);
    } catch (error) {
      console.error('Error fetching quotes:', error);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/quotes/${id}/status?status=${status}`);
      toast.success('Durum güncellendi!');
      fetchQuotes();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Bir hata oluştu!');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-700';
      case 'approved':
        return 'bg-green-100 text-green-700';
      case 'rejected':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending':
        return 'Beklemede';
      case 'approved':
        return 'Onaylandı';
      case 'rejected':
        return 'Reddedildi';
      default:
        return status;
    }
  };

  return (
    <AdminLayout>
      <div>
        <h1 
          className="text-3xl font-bold text-gray-900 mb-8"
          style={{ fontFamily: 'Space Grotesk, sans-serif' }}
          data-testid="quotes-title"
        >
          Teklif Talepleri
        </h1>

        <div className="space-y-4">
          {quotes.map((quote) => (
            <div 
              key={quote.id} 
              className="bg-white rounded-xl border border-gray-200 p-6"
              data-testid={`quote-card-${quote.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Building className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{quote.company_name}</h3>
                    <p className="text-sm text-gray-600">{quote.contact_person}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500 mb-2">
                    {format(new Date(quote.created_at), 'dd MMM yyyy HH:mm', { locale: tr })}
                  </p>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(quote.status)}`}>
                    {getStatusLabel(quote.status)}
                  </span>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-4 pl-15">
                <div>
                  <p className="text-sm text-gray-500">E-posta</p>
                  <p className="text-gray-900">{quote.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Telefon</p>
                  <p className="text-gray-900">{quote.phone}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Hizmet Türü</p>
                  <p className="text-gray-900">{quote.service_type}</p>
                </div>
                {quote.budget_range && (
                  <div>
                    <p className="text-sm text-gray-500">Bütçe</p>
                    <p className="text-gray-900">{quote.budget_range}</p>
                  </div>
                )}
              </div>

              <div className="mb-4 pl-15">
                <p className="text-sm text-gray-500 mb-1">Proje Detayları</p>
                <p className="text-gray-700">{quote.project_details}</p>
              </div>

              <div className="flex gap-2 pl-15">
                <Select value={quote.status} onValueChange={(value) => updateStatus(quote.id, value)}>
                  <SelectTrigger className="w-40" data-testid={`quote-status-${quote.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Beklemede</SelectItem>
                    <SelectItem value="approved">Onayla</SelectItem>
                    <SelectItem value="rejected">Reddet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminQuotes;